/**
 * 아이콘 버튼 공통 styled-components
 *
 * 반복되는 아이콘 버튼 패턴을 통합하여 중복을 제거한다.
 * - Header.tsx (IconButton)
 * - SearchBar.tsx (SearchIconButton, ClearButton)
 * - CopyButton.tsx (CopyButtonWrapper)
 * - Inputs.tsx (ToggleButton)
 * - BottomPlayer.tsx (CloseButton)
 */

import styled, { css } from 'styled-components';

// ─────────────────────────────────────────────────────────────
// 타입 정의
// ─────────────────────────────────────────────────────────────

export type IconButtonVariant = 'default' | 'subtle' | 'primary' | 'danger';
export type IconButtonSize = 'sm' | 'md' | 'lg';

interface IconButtonProps {
  $variant?: IconButtonVariant;
  $size?: IconButtonSize;
  $circular?: boolean;
  disabled?: boolean;
}

// ─────────────────────────────────────────────────────────────
// 크기별 스타일
// ─────────────────────────────────────────────────────────────

const sizeStyles = {
  sm: css`
    padding: 4px;
    min-width: 28px;
    min-height: 28px;
  `,
  md: css`
    padding: 8px;
    min-width: 36px;
    min-height: 36px;
  `,
  lg: css`
    padding: 12px;
    min-width: 44px;
    min-height: 44px;
  `,
};

// ─────────────────────────────────────────────────────────────
// variant별 스타일
// ─────────────────────────────────────────────────────────────

const variantStyles = {
  /** 기본 — 배경 없음, 호버 시 배경 표시 */
  default: css`
    background: none;
    color: ${({ theme }) => theme.colors.text};

    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.hover};
    }

    &:active:not(:disabled) {
      background: ${({ theme }) => theme.colors.active};
    }
  `,

  /** subtle — 보조 배경색 */
  subtle: css`
    background: ${({ theme }) => theme.colors.backgroundSecondary};
    color: ${({ theme }) => theme.colors.textSecondary};

    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.hover};
      color: ${({ theme }) => theme.colors.text};
    }
  `,

  /** primary — 브랜드 색상 배경 */
  primary: css`
    background: ${({ theme }) => theme.colors.primary};
    color: white;

    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.primaryHover};
    }

    &:active:not(:disabled) {
      background: ${({ theme }) => theme.colors.primaryActive};
    }
  `,

  /** danger — 삭제·위험 액션용 */
  danger: css`
    background: none;
    color: ${({ theme }) => theme.colors.error};

    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.errorLight};
    }

    &:active:not(:disabled) {
      background: ${({ theme }) => theme.colors.errorDark};
      color: white;
    }
  `,
};

// ─────────────────────────────────────────────────────────────
// 기본 아이콘 버튼
// ─────────────────────────────────────────────────────────────

export const IconButton = styled.button<IconButtonProps>`
  /* 리셋 */
  border: none;
  outline: none;
  cursor: pointer;

  /* 레이아웃 */
  display: inline-flex;
  align-items: center;
  justify-content: center;

  /* 모양 */
  ${({ $circular }) =>
    $circular
      ? css`border-radius: 50%;`
      : css`border-radius: 8px;`}

  /* 크기 */
  ${({ $size = 'md' }) => sizeStyles[$size]}

  /* variant */
  ${({ $variant = 'default' }) => variantStyles[$variant]}

  /* 비활성화 */
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* 키보드 포커스 링 */
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  transition: all 0.2s ease;
  user-select: none;
`;

// ─────────────────────────────────────────────────────────────
// 특수 목적 아이콘 버튼
// ─────────────────────────────────────────────────────────────

/**
 * 원형 아이콘 버튼 (가장 흔한 패턴)
 * 대체: Header IconButton, BottomPlayer CloseButton
 */
export const CircularIconButton = styled(IconButton)`
  border-radius: 50%;
`;

/**
 * subtle 아이콘 버튼 (검색 바, 입력 필드용)
 * 대체: SearchBar SearchIconButton
 */
export const SubtleIconButton = styled(IconButton).attrs({
  $variant: 'subtle',
})``;

/**
 * 닫기 버튼 (X 버튼 패턴)
 * 대체: BottomPlayer CloseButton, 모달 닫기 버튼
 */
export const CloseButton = styled(CircularIconButton).attrs({
  $variant: 'default',
  $size: 'sm',
})`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
`;

/**
 * 토글 버튼 (비밀번호 표시/숨김 등)
 * 대체: Inputs.tsx ToggleButton
 */
export const ToggleIconButton = styled(IconButton).attrs({
  $variant: 'subtle',
  $size: 'sm',
})`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
`;

/**
 * 클리어 버튼 (입력 필드 초기화)
 * 대체: SearchBar ClearButton
 */
export const ClearIconButton = styled(IconButton).attrs({
  $variant: 'subtle',
  $size: 'sm',
  $circular: true,
})`
  color: #A3A3A3;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.text};
  }
`;

// ─────────────────────────────────────────────────────────────
// 배지가 있는 아이콘 버튼 (장바구니, 알림 등)
// ─────────────────────────────────────────────────────────────

export const IconButtonBadgeContainer = styled.div`
  position: relative;
  display: inline-flex;
`;

/** 숫자 배지 (count === 0 이면 숨김) */
export const IconButtonBadge = styled.span<{ count?: number }>`
  position: absolute;
  top: -4px;
  right: -4px;

  min-width: 18px;
  height: 18px;
  padding: 0 4px;

  display: flex;
  align-items: center;
  justify-content: center;

  background: ${({ theme }) => theme.colors.error};
  color: white;

  font-size: 10px;
  font-weight: 600;

  border-radius: 9px;
  border: 2px solid ${({ theme }) => theme.colors.background};

  ${({ count }) => count === 0 && css`display: none;`}
`;

export default {
  IconButton,
  CircularIconButton,
  SubtleIconButton,
  CloseButton,
  ToggleIconButton,
  ClearIconButton,
  IconButtonBadgeContainer,
  IconButtonBadge,
};
